import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';

import { ContadorOperarService } from '../../../../../services/contador/contador-operar.service';
import { EmpresaConfigService } from '../../../../../services/configuracoes/empresa.service';
import { AtividadeEconomicaService } from '../../../../../services/configuracoes/atividade-economica.service';
import { CidadeService } from '../../../../../services/configuracoes/cidade.service';
import {
  ConfiguracaoEmpresaContadorConsultaDTO,
  ConfiguracaoEmpresaContadorDTO,
} from '../../../../../models/contador/configuracao-empresa-contador.model';

/** Opcao do multiselect de atividade economica. */
interface AtividadeOption {
  label: string;
  value: number;
}

/** Opcao do dropdown de cidade. */
interface CidadeOption {
  label: string;
  value: number;
}

/**
 * Aba "Configuração da Empresa" da tela Operar Cliente (Área do Contador).
 *
 * Busca/altera um SUBCONJUNTO da configuração da empresa do cliente via o
 * endpoint enxuto `/configuracaoEmpresa/contador` (resolvido pelo contexto no
 * backend). O arquivo do certificado vai pelo endpoint multipart existente.
 *
 * `clienteId` é só um gatilho de recarga quando o contador troca de cliente; o
 * header X-Empresa-Contexto é injetado pelo interceptor a partir do contexto.
 */
@Component({
  selector: 'app-operar-config-empresa',
  templateUrl: './config-empresa.component.html',
  styleUrls: ['./config-empresa.component.scss'],
})
export class OperarConfigEmpresaComponent implements OnInit, OnChanges {
  /** Id da empresa do cliente — muda quando o contador troca de cliente. */
  @Input() clienteId?: number | null;

  carregando = false;
  erro = false;
  salvando = false;

  /** Modelo do formulário (subconjunto editável). */
  form: ConfiguracaoEmpresaContadorDTO = this.formVazio();

  /** Nome do certificado já cadastrado (somente leitura). */
  nomeCertificadoAtual: string | null = null;
  vencimentoCertificadoAtual: string | null = null;

  /** Arquivo de certificado novo selecionado (opcional). */
  certificadoFile: File | null = null;

  /** Opções de atividade econômica (catálogo global). */
  atividadeOptions: AtividadeOption[] = [];

  /**
   * Indica se o catálogo de atividades foi carregado com sucesso. Enquanto
   * `false` (carga pendente ou falha), o PUT NÃO envia `atividadesEconomicasId`
   * para não apagar os vínculos atuais do cliente por engano.
   */
  atividadesCarregadas = false;

  /** Opções de cidade (catálogo global). */
  cidadeOptions: CidadeOption[] = [];

  submetido = false;

  constructor(
    private operarService: ContadorOperarService,
    private empresaService: EmpresaConfigService,
    private atividadeService: AtividadeEconomicaService,
    private cidadeService: CidadeService,
    private messageService: MessageService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.carregarAtividades();
    this.carregarCidades();
    this.carregar();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Recarrega ao trocar de cliente (depois do primeiro load do ngOnInit).
    if (changes['clienteId'] && !changes['clienteId'].firstChange) {
      this.carregar();
    }
  }

  private formVazio(): ConfiguracaoEmpresaContadorDTO {
    return {
      nome: '',
      fantasia: '',
      endereco: null,
      codigoEstabelecimento: null,
      idCsc: null,
      csc: null,
      senhaCertificadoDigital: null,
      atividadesEconomicasId: [],
      cidadeId: null,
    };
  }

  /** Carrega o catálogo global de cidades para o dropdown. */
  private carregarCidades(): void {
    this.cidadeService.get('/cidade?listarTodos=true').subscribe({
      next: (res) => {
        this.cidadeOptions = (res.content || [])
          .filter((c) => c.id != null)
          .map((c) => ({ label: c.nome, value: Number(c.id) }));
      },
      error: () => {
        this.cidadeOptions = [];
      },
    });
  }

  /** Carrega o catálogo global de atividades econômicas para o multiselect. */
  private carregarAtividades(): void {
    this.atividadeService.get('/atividadeEconomica', { listarTodos: true }).subscribe({
      next: (res) => {
        this.atividadeOptions = (res.content || [])
          .filter((a) => a.id != null)
          .map((a) => ({ label: `${a.codigo} - ${a.nome}`, value: a.id as number }));
        this.atividadesCarregadas = true;
      },
      error: () => {
        this.atividadeOptions = [];
        this.atividadesCarregadas = false;
      },
    });
  }

  /** Carrega a configuração da empresa do cliente (sob contexto). */
  carregar(): void {
    this.carregando = true;
    this.erro = false;
    this.submetido = false;
    this.certificadoFile = null;

    this.operarService.buscarConfiguracao().subscribe({
      next: (res) => {
        this.aplicar(res);
        this.carregando = false;
      },
      error: () => {
        this.form = this.formVazio();
        this.nomeCertificadoAtual = null;
        this.vencimentoCertificadoAtual = null;
        this.erro = true;
        this.carregando = false;
      },
    });
  }

  private aplicar(dto: ConfiguracaoEmpresaContadorConsultaDTO): void {
    this.form = {
      nome: dto.nome ?? '',
      fantasia: dto.fantasia ?? '',
      endereco: dto.endereco ?? null,
      codigoEstabelecimento: dto.codigoEstabelecimento ?? null,
      idCsc: dto.idCsc ?? null,
      csc: dto.csc ?? null,
      // O GET nunca retorna a senha do certificado (segurança). O campo começa
      // sempre vazio; se ficar em branco, o PUT mantém a senha atual.
      senhaCertificadoDigital: null,
      atividadesEconomicasId: dto.atividadesEconomicasId ?? [],
      cidadeId: dto.cidadeId ?? null,
    };
    this.nomeCertificadoAtual = dto.nomeCertificadoDigital ?? null;
    this.vencimentoCertificadoAtual = dto.vencimentoCertificadoDigital ?? null;
  }

  /** Seleção de arquivo de certificado (.pfx/.p12), validando extensão/tamanho. */
  onCertificadoSelecionado(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files.length ? input.files[0] : null;
    if (!file) {
      this.certificadoFile = null;
      return;
    }
    const nome = file.name.toLowerCase();
    if (!nome.endsWith('.pfx') && !nome.endsWith('.p12')) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('contador_operar.config.certificado_invalido'),
      });
      input.value = '';
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('contador_operar.config.certificado_grande'),
      });
      input.value = '';
      return;
    }
    this.certificadoFile = file;
  }

  private formInvalido(): boolean {
    return !this.form.nome?.trim() || !this.form.fantasia?.trim();
  }

  /** Salva: sobe o certificado (se houver) e depois persiste o subconjunto. */
  salvar(): void {
    this.submetido = true;
    if (this.formInvalido()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('contador_operar.config.campos_obrigatorios'),
      });
      return;
    }

    const senha = this.form.senhaCertificadoDigital?.trim() ?? '';

    // Um certificado novo exige a senha nova: o backend precisa dela para abrir o
    // arquivo .pfx/.p12. Sem senha, avisa o usuário e não segue com o upload.
    if (this.certificadoFile && !senha) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('contador_operar.config.senha_certificado_obrigatoria'),
      });
      return;
    }

    this.salvando = true;

    if (this.certificadoFile) {
      this.empresaService.uploadCertificadoDigitalMultipart(this.certificadoFile, senha).subscribe({
        next: () => this.persistir(),
        error: (err: any) => {
          this.salvando = false;
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('contador_operar.config.erro_certificado'),
            detail: err?.error?.message,
          });
        },
      });
    } else {
      this.persistir();
    }
  }

  /**
   * Monta o corpo do PUT respeitando a semântica "manter vs. alterar" do backend:
   * - `senhaCertificadoDigital`: só vai quando o usuário digitou uma senha nova;
   *   vazio/null => o backend mantém a senha atual (nunca zera).
   * - `atividadesEconomicasId`: só substitui os vínculos quando o catálogo carregou
   *   com sucesso; se a carga falhou, envia null para não apagar os vínculos atuais.
   */
  private montarPayload(): ConfiguracaoEmpresaContadorDTO {
    const senha = this.form.senhaCertificadoDigital?.trim();
    return {
      nome: this.form.nome,
      fantasia: this.form.fantasia,
      endereco: this.form.endereco ?? null,
      codigoEstabelecimento: this.form.codigoEstabelecimento ?? null,
      idCsc: this.form.idCsc ?? null,
      csc: this.form.csc ?? null,
      senhaCertificadoDigital: senha ? senha : null,
      atividadesEconomicasId: this.atividadesCarregadas
        ? (this.form.atividadesEconomicasId ?? [])
        : null,
      cidadeId: this.form.cidadeId ?? null,
    };
  }

  private persistir(): void {
    this.operarService.atualizarConfiguracao(this.montarPayload()).subscribe({
      next: (res) => {
        this.salvando = false;
        this.certificadoFile = null;
        this.aplicar(res);
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('contador_operar.config.sucesso'),
        });
      },
      error: (err) => {
        this.salvando = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('contador_operar.config.erro_salvar'),
          detail: err?.error?.message,
        });
      },
    });
  }
}
